import { useContext, useEffect, useState } from "react";
import { Box, alpha, lighten, IconButton, styled, useTheme, Button, Avatar, Menu, MenuItem, Typography } from "@mui/material";
import MenuTwoToneIcon from "@mui/icons-material/MenuTwoTone";
import { SidebarContext } from "src/contexts/SidebarContext";
import CloseTwoToneIcon from "@mui/icons-material/CloseTwoTone";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { useNavigate } from "react-router";
import LogOutButton from "src/components/LogComponents/LogOutButton";
import useDoLogout from "src/utils/useDoLogout";
import { useAuth0 } from "@auth0/auth0-react";

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        right: 0;
        z-index: 6;     
        background: ${theme.colors.alpha.black[100]};
        backdrop-filter: blur(3px);
        position: fixed;
        justify-content: space-between;
        width: 100%;
        border-bottom: 1px solid ${theme.colors.alpha.white[30]};
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            left: ${theme.sidebar.width};
            width: auto;
        }
`
);

const navItems = [
  { label: "Home", path: "/" },
  // { label: "Pricing", path: "/pricing" }, //DESCOMENTAR CUANDO SE PRECISE PAGINA DE PRECIOS NUEVAMENTE
  { label: "Contact", path: "/contact" },
  // { label: "API Docs", path: `${process.env.REACT_APP_API_DOCUMENTATION}`, external: true }, //DESCOMENTAR CUANDO SE PRECISE PAGINA DE API DOCS NUEVAMENTE
  // { label: "Chat", path: `https://chat.rotogpt.com/`, external: true },
];

function Header() {
  const { sidebarToggle, toggleSidebar, closeSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const doLogOut = useDoLogout();
  const { user } = useAuth0();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleNavClick = (path: string, external: boolean = false) => {
    if (external) {
      window.open(path, "_blank");
    } else {
      navigate(path);
    }
    closeSidebar();
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    doLogOut();
    handleMenuClose();
  };

  //Using this approach to close the menu in case of a click/touch outside the menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (anchorEl && !anchorEl.contains(event.target)) {
        handleMenuClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl]);

  const userData = user;

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      sx={{
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 1px 0 ${alpha(lighten(theme.colors.primary.main, 0.7), 0.15)}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(theme.colors.alpha.black[100], 0.2)}, 0px 5px 22px -4px ${alpha(theme.colors.alpha.black[100], 0.1)}`,
      }}
    >
      <Box display="flex" alignItems="center">
        {/* Button menu for mobile */}
        <Box
          component="span"
          sx={{
            ml: 0,
            display: { lg: "none", xs: "inline-block" },
          }}
        >
          <IconButton
            sx={{ mr: 0, pr: 0, pl: 1.5, color: `${PureLightThemeCaaS.colors.alpha.white[100]}`, cursor: "pointer" }}
            onClick={sidebarToggle ? closeSidebar : toggleSidebar}
          >
            {!sidebarToggle ? <MenuTwoToneIcon fontSize="small" /> : <CloseTwoToneIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* Navbar in header */}
      <Box sx={{ display: { lg: "flex", xs: "none" }, alignItems: "center", gap: 0, ml: { lg: "auto", xs: 0 } }}>
        {/* AGREGAR external LUEGO DE LABEL,PATH CUANDO SE AGREGUE API DOCS */}
        {navItems.map(({ label, path }) => (
          //AGREGAR EXTERNAL CUANDO SE AGREGUE API DOCS COMO PARAMETRO EN handleNavClick LUEGO DE PATH
          <Button
            key={label}
            sx={{ p: { lg: 1, xs: 0 }, textAlign: "center", color: "#ffffff", fontSize: { lg: "14px", xs: "12px" }, fontFamily: "Roboto, Arial" }}
            onClick={() => handleNavClick(path)}
          >
            {label}
          </Button>
        ))}
      </Box>

      <Box
        p={0}
        sx={{
          display: { xs: "block", lg: "none" },
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        <a href="/">
          <img src="/RotoGPT.png" alt="RotoGPT" width={100} />
        </a>
      </Box>

      {/* Log out button redirect to "/" */}
      {/* <Box mr={1}>
        <LogOutButton />
      </Box> */}

      {/* Log out menu redirect to "/" */}
      <Box pr={3} pl={1}>
        <IconButton onClick={handleAvatarClick} sx={{ p: 0, textAlign: "center" }}>
          <Avatar alt="User Avatar" src={userData.picture} />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
              boxShadow: "none",
              border: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
              borderRadius: 0.5,
            },
          }}
        >
          <Typography
            variant="h6"
            pb={1}
            fontWeight="bold"
            fontFamily="Roboto, Arial"
            textAlign="center"
            color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}
            sx={{
              width: "auto",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {/* This is for use only the first word in the name */}
            Hi {userData.name.split(" ")[0]}
          </Typography>
          <MenuItem
            onClick={handleLogout}
            sx={{
              textAlign: "center",

              fontWeight: "bold",
              fontFamily: "Roboto, Arial",
              minHeight: { lg: "30px", xs: "25px" },
              width: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            Log out
          </MenuItem>
        </Menu>
      </Box>
    </HeaderWrapper>
  );
}

export default Header;
